import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Activity, Lock } from 'lucide-react';
import { login, getSessionExpiredFlag, clearSessionExpiredFlag } from '../api';
import { useAuth } from '../context/AuthContext';

function Login() {
  const navigate = useNavigate();
  const { loginSuccess } = useAuth();
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);

  useEffect(() => {
    document.title = 'Sign in | Dental Events';
    return () => { document.title = 'Dental Events'; };
  }, []);

  useEffect(() => {
    if (getSessionExpiredFlag()) {
      setSessionExpired(true);
      clearSessionExpiredFlag();
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const { data } = await login(password);
      const token = data?.data?.token;
      if (token) {
        loginSuccess(token);
        navigate('/', { replace: true });
      } else {
        setError('Invalid response from server');
      }
    } catch (err) {
      const message = err.response?.data?.message || err.message || 'Login failed';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
      <div className="w-full max-w-sm">
        <div className="card">
          <div className="flex flex-col items-center mb-6">
            <div className="p-3 rounded-full bg-dental-100 mb-3">
              <Activity className="w-10 h-10 text-dental-600" />
            </div>
            <h1 className="text-xl font-bold text-gray-900">Dental Events</h1>
            <p className="text-sm text-gray-500">Competitor Tracker</p>
          </div>

          {sessionExpired && (
            <div className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm" role="status">
              Your session expired. Please sign in again.
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  className="input pl-10"
                  placeholder="Enter password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={submitting}
                  autoFocus
                  aria-describedby={error ? 'login-error' : undefined}
                  aria-invalid={!!error}
                />
              </div>
            </div>

            {error && (
              <p id="login-error" className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg" role="alert">
                {error}
              </p>
            )}

            <button
              type="submit"
              className="btn-primary w-full flex items-center justify-center gap-2"
              disabled={submitting || !password.trim()}
            >
              {submitting ? 'Signing in…' : 'Sign in'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default Login;
